import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Icon, TooltipComponent } from "../../../components/Component";
import {
  Button,
  Card,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { Button as AntButton, Popconfirm, Segmented, Table, Tabs } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import SearchInput from "../../form-components/SearchComponent";
import ProjectScopeSelect, {
  ORG_MASTER_PROJECT_ID,
} from "../shared/ProjectScopeSelect";
import {
  allGroupKeys,
  buildNormGroupTree,
  withGroupRows,
} from "../shared/normGroupTree";
import "../shared/project.css";
import { useDispatch, useSelector } from "react-redux";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import ManpowerNormModal from "./ManpowerNormModal";
import ManpowerNormUploadModal from "./ManpowerNormUploadModal";
import ManpowerNormsHistoryDrawer from "./ManpowerNormsHistoryDrawer";
import ManpowerNormsExportModal from "./ManpowerNormsExportModal";
import { manpowerApis } from "../../../api/ManpowerNorms";
import TopMenu from "../../../route/topMenu";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import {
  AddAccess,
  EditAccess,
  ViewAccess,
} from "../../../components/accessControl/AccessControl";

function ManpowerNormList() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  // Attention mark on the History button: amber=generating, green=ready,
  // red=failed. Cleared when the user opens the History drawer's Exports tab.
  const exportStatus: "idle" | "pending" | "ready" | "failed" = useSelector(
    (state: any) => state.highlight?.exportStatus || "idle"
  );
  const exportMark: { color: string; title: string } | null =
    exportStatus === "pending"
      ? { color: "#f4bd0e", title: "An export is generating…" }
      : exportStatus === "ready"
      ? { color: "#1ee0ac", title: "An export is ready to download" }
      : exportStatus === "failed"
      ? { color: "#e85347", title: "An export failed — check History" }
      : null;

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyDefaultTab, setHistoryDefaultTab] = useState<
    "imports" | "exports"
  >("imports");
  const [exportOpen, setExportOpen] = useState(false);

  // Add/edit form modal — a flat manpower type, no material lines.
  const [modalOpen, setModalOpen] = useState<{ status: boolean; id: number | null }>({
    status: false,
    id: null,
  });

  const toggleStatus = async (record: any) => {
    try {
      setLoading(true);
      const apiResponse =
        record?.status === "Active"
          ? await manpowerApis.deactivate(Number(record?.manpowerNormId))
          : await manpowerApis.activate(Number(record?.manpowerNormId));
      if (apiResponse?.status) {
        refreshData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error updating manpower norm status:", error);
      setLoading(false);
    }
  };

  const columns: any = [
    {
      title: "Norm Group",
      key: "normGroup",
      width: 200,
      render: (_: any, record: any) =>
        record?.groupCode ? (
          <div>
            <div style={{ color: "#022c57" }}>{record.groupName || record.groupCode}</div>
            <span style={{ fontSize: 11.5, color: "#8094ae" }}>
              {record.groupCode}
              {record.level1Code && record.level1Code !== record.groupCode
                ? ` · ${record.level1Code}`
                : ""}
            </span>
          </div>
        ) : (
          <span style={{ color: "#c9d2e3" }}>—</span>
        ),
    },
    {
      title: "Item Code",
      dataIndex: "itemCode",
      key: "itemCode",
      width: 180,
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`manpower-${record?.manpowerNormId}`}
              onClick={() => {
                setModalOpen({ status: true, id: record?.manpowerNormId });
              }}
            >
              {text}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`manpower-${record?.manpowerNormId}`}
              direction="right"
              text={"Click To Edit " + record?.itemCode}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Item Description",
      dataIndex: "itemDescription",
      key: "itemDescription",
      width: 260,
      render: (text: any) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Units",
      dataIndex: "uomName",
      key: "uomName",
      width: 130,
      render: (text: any, record: any) => <span>{text || record?.uomId || "-"}</span>,
    },
    {
      title: "PUC Factor",
      dataIndex: "pucFactor",
      key: "pucFactor",
      width: 120,
      render: (value: any) =>
        value !== undefined && value !== null && value !== ""
          ? Number(value)
          : "-",
    },
    {
      title: "Unit Price (₹)",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 130,
      render: (value: any) =>
        value !== undefined && value !== null && value !== ""
          ? Number(value)
          : "-",
    },
    {
      title: "Created By",
      dataIndex: "createdByUserName",
      key: "createdByUserName",
      width: 150,
      render: (text: any) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (text: any) => (text ? dayjs(text).format("DD-MMM-YYYY") : "N/A"),
    },
    {
      title: "Updated On",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (text: any) => (text ? dayjs(text).format("DD-MMM-YYYY") : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text: any) => {
        return (
          <div className="justify-start align-center">
            <h6
              className={`tb-status ms-1`}
              style={{
                color: `${text == "Active" ? "green" : "red"}`,
                fontSize: "14px",
              }}
            >
              {text == "Active" ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )}{" "}
              {text}
            </h6>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (text: any, record: any) => {
        const isActive = record?.status === "Active";
        return (
          <EditAccess moduleName="Norms">
            <Popconfirm
              title={`${isActive ? "Deactivate" : "Activate"} Manpower Norm`}
              description={`Are you sure you want to ${
                isActive ? "deactivate" : "activate"
              } "${record?.itemCode}"?`}
              okText="Yes"
              cancelText="No"
              onConfirm={() => toggleStatus(record)}
            >
              <Button size="sm" color="" className="btn-dim">
                <span
                  className="inlineTableCellLink"
                  style={{ color: isActive ? "red" : "green" }}
                >
                  {isActive ? "Deactivate" : "Activate"}
                </span>
              </Button>
            </Popconfirm>
          </EditAccess>
        );
      },
    },
  ];

  const methods = useForm({ mode: "onChange" });
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);
  // Scope: which project's rows (0 = organization master) and which format tab.
  // Both are threaded into every list/create/upload/export/duplicate call.
  const [selectedProjectId, setSelectedProjectId] = useState<number>(
    ORG_MASTER_PROJECT_ID
  );
  const [category, setCategory] = useState<"PRW" | "OTHERS">("PRW");
  // Grouped by norm group is the default view; List is the escape hatch.
  // Tree mode loads every norm rather than a page — a tree over 25
  // keyset-paginated rows would show only the groups on that page and re-open
  // the same group again on page 2.
  const [treeMode, setTreeMode] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const TREE_PAGE_SIZE = 1000;

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;
      const wantsAll = params.treeMode ?? treeMode;
      const apiResponse = await manpowerApis.getList({
        page: wantsAll ? 1 : current,
        pageSize: wantsAll ? TREE_PAGE_SIZE : pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        // Explicit override (scope-change handlers) wins over the current state
        // to avoid the stale-closure race when project/tab changes reset page 1.
        projectId: params.projectId ?? selectedProjectId,
        category: params.category ?? category,
      });
      const { data, status } = apiResponse;
      if (status) {
        const { list, count } = data;
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));
        setDataSource((prev: any) =>
          append ? [...prev, ...(list || [])] : list || []
        );
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Derived from whatever the current fetch returned, so search and the group
  // filter narrow the tree exactly as they narrow the flat list.
  const treeData = treeMode
    ? buildNormGroupTree(dataSource || [], "manpowerNormId")
    : [];
  // Not memoised: `columns` is rebuilt every render, so a useMemo keyed on it
  // would never hit. It is a map over ~7 column defs.
  // The Norm Group column repeats what the parent rows already say, so it is
  // dropped in tree mode and kept in List mode where nothing else shows it.
  const treeColumns = withGroupRows(
    columns.filter((column: any) => column?.key !== "normGroup")
  );

  useEffect(() => {
    // Fully collapsed on a fresh dataset: the 11 group headers ARE the
    // overview, and expanding is one click.
    if (treeMode) setExpandedKeys([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeMode, dataSource]);

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId
            : dataSource[0]?.paginationId;
        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({ ...apiParams });
  };

  const refreshListView = () => {
    try {
      setPagination({
        current: 1,
        pageSize: 10,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData({ pageDiff: 0 });
    } catch (error) {}
  };

  useEffect(() => {
    dispatch(updateMenuData(TopMenu?.activeProjects));
    refreshListView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      ...overRideData,
    });
  };

  // Reset to page 1 and reload for a scope (project + category) change.
  const loadScoped = (
    nextProjectId: number,
    nextCategory: "PRW" | "OTHERS"
  ) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({
      oldPage: 1,
      current: 1,
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
      projectId: nextProjectId,
      category: nextCategory,
      treeMode,
    });
  };

  const onTreeModeChange = (next: boolean) => {
    setTreeMode(next);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({
      oldPage: 1,
      current: 1,
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
      projectId: selectedProjectId,
      category,
      treeMode: next,
    });
  };

  const onProjectChange = (projectId: number) => {
    setSelectedProjectId(projectId);
    loadScoped(projectId, category);
  };

  const onCategoryChange = (key: string) => {
    const nextCategory = key as "PRW" | "OTHERS";
    setCategory(nextCategory);
    loadScoped(selectedProjectId, nextCategory);
  };

  // Copy the organization-master set into the selected project (projectId > 0).
  const handleDuplicate = () => {
    if (selectedProjectId <= 0) return;
    Swal.fire({
      icon: "question",
      title: "Duplicate organization master set?",
      text: "This copies the organization-master manpower norms into the selected project.",
      showCancelButton: true,
      confirmButtonText: "Duplicate",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        setLoading(true);
        const response = await manpowerApis.duplicateToProject(
          selectedProjectId
        );
        const { status, data, message } = response || {};
        if (status) {
          Swal.fire({
            icon: "success",
            title: "Duplicated Successfully",
            text:
              data?.message ||
              `${data?.insertedCount ?? 0} manpower norm(s) copied.`,
            confirmButtonText: "Ok",
          });
          refreshData();
        } else {
          setLoading(false);
          Swal.fire({
            icon: "error",
            title: "Duplication Failed",
            text: message || "Could not duplicate the organization set.",
            confirmButtonText: "Ok",
          });
        }
      } catch (error) {
        console.error("Error duplicating manpower norms:", error);
        setLoading(false);
      }
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  useEffect(() => {
    const navState = location.state as any;
    if (navState?.fromNav) {
      setModalOpen({ status: false, id: null });
    }
  }, [location.key]);

  return (
    <>
      <Head title={"Manpower Norms"} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              <FormProvider {...methods}>
                <form>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h5
                      className="mb-0"
                      style={{ color: "#022c57", fontWeight: 600 }}
                    >
                      Manpower Norms
                    </h5>
                    <div className="d-flex align-items-center">
                      <span
                        className="me-2 text-soft"
                        style={{ fontWeight: 500 }}
                      >
                        Scope
                      </span>
                      <ProjectScopeSelect
                        value={selectedProjectId}
                        onChange={onProjectChange}
                      />
                    </div>
                  </div>
                  <Tabs
                    activeKey={category}
                    onChange={onCategoryChange}
                    items={[
                      { key: "PRW", label: "PRW" },
                      { key: "OTHERS", label: "Others" },
                    ]}
                  />
                  <div
                    className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                    style={{ minHeight: "40px", border: "0px" }}
                  >
                    <div className="nk-ibx-head-actions w-45 ms-1">
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                        <div className={`w-100 m-0 `}>
                          <SearchInput
                            name="search"
                            placeholder="Search Item Code / Description"
                            width="100%"
                            prefixIcon={<SearchOutlined />}
                            onDebouncedSearch={async (value: any) => {
                              setsearchQuery(value);
                              setPagination({ ...pagination, current: 1 });
                              await fetchData({
                                oldPage: 1,
                                current: 1,
                                sortOrder: "DESC",
                                pageSize: pagination.pageSize.toString(),
                                direction: "P",
                                pageDiff: 0,
                                paginationId: null,
                                searchQuery: value,
                              });
                            }}
                            debounceDelay={300}
                          />
                        </div>
                      </ul>
                    </div>
                      <div className="d-flex align-items-center gap-2 ms-2">
                        <Segmented
                          size="middle"
                          value={treeMode ? "tree" : "flat"}
                          onChange={(value) => onTreeModeChange(value === "tree")}
                          options={[
                            { label: "Group", value: "tree" },
                            { label: "List", value: "flat" },
                          ]}
                        />
                        {/* Rendered in both modes, disabled in List: showing
                            and hiding them resized the toolbar and made the
                            whole page jump on every mode switch. */}
                        <AntButton
                          type="link"
                          size="small"
                          disabled={!treeMode}
                          onClick={() => setExpandedKeys(allGroupKeys(treeData))}
                        >
                          Expand all
                        </AntButton>
                        <AntButton
                          type="link"
                          size="small"
                          disabled={!treeMode}
                          onClick={() => setExpandedKeys([])}
                        >
                          Collapse all
                        </AntButton>
                      </div>
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="dropdown-toggle btn btn-icon btn-trigger"
                            onClick={() => {
                              refreshListView();
                            }}
                          >
                            <Icon name="redo"></Icon>
                          </DropdownToggle>
                        </UncontrolledDropdown>
                      </li>
                      <li className="pt-0 pb-0">
                        <ViewAccess moduleName="Norms">
                          <Button
                            className="toggle"
                            color="light"
                            size="sm"
                            outline
                            onClick={() => {
                              setExportOpen(true);
                            }}
                          >
                            <Icon name={"download"} />
                            <span>Export Excel</span>
                          </Button>
                        </ViewAccess>
                      </li>
                      <li className="pt-0 pb-0">
                        <span
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <Button
                            className="toggle"
                            color="light"
                            size="sm"
                            outline
                            onClick={() => {
                              setHistoryDefaultTab("imports");
                              setHistoryDrawerOpen(true);
                            }}
                          >
                            <Icon name={"history"} />
                            <span>History</span>
                          </Button>
                          {exportMark && (
                            <span
                              title={exportMark.title}
                              style={{
                                position: "absolute",
                                top: -3,
                                right: -3,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: exportMark.color,
                                border: "2px solid #fff",
                                boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
                              }}
                            />
                          )}
                        </span>
                      </li>
                      {selectedProjectId > 0 && (
                        <li className="pt-0 pb-0">
                          <AddAccess moduleName="Norms">
                            <Button
                              className="toggle"
                              color="light"
                              size="sm"
                              outline
                              onClick={handleDuplicate}
                            >
                              <Icon name={"copy"} />
                              <span>Duplicate org set → project</span>
                            </Button>
                          </AddAccess>
                        </li>
                      )}
                      <li className="pt-0 pb-0">
                        <AddAccess moduleName="Norms">
                          <Button
                            className="toggle"
                            color="light"
                            size="sm"
                            outline
                            onClick={() => {
                              setUploadModalOpen(true);
                            }}
                          >
                            <Icon name={"upload"} />
                            <span>Upload Manpower</span>
                          </Button>
                        </AddAccess>
                      </li>
                      <li className="pt-0 pb-0">
                        <AddAccess moduleName="Norms">
                          <Button
                            className="toggle"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              setModalOpen({ status: true, id: null });
                            }}
                          >
                            <Icon name={"plus"} />
                            <span>Add Manpower</span>
                          </Button>
                        </AddAccess>
                      </li>
                    </ul>
                  </div>
                </form>
              </FormProvider>

              <div className="card-inner card-inner-lg p-0">
                <div className="content clearfix">
                  <div
                    style={{
                      width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
                      margin: "10px auto",
                    }}
                  >
                    <SkeletonTable
                      loading={loading}
                      columns={columns as SkeletonTableColumnsType[]}
                    >
                      {treeMode ? (
                        <Table
                          rowKey="rowKey"
                          columns={treeColumns}
                          dataSource={treeData}
                          pagination={false}
                          expandable={{
                            expandedRowKeys: expandedKeys,
                            onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                          }}
                          rowClassName={(record: any) =>
                            record?.isGroupRow ? "pms-norm-group-row" : ""
                          }
                          bordered
                          size="middle"
                          scroll={{ x: "max-content" }}
                        />
                      ) : (
                        <Table
                          rowKey="manpowerNormId"
                          columns={columns}
                          dataSource={dataSource}
                          pagination={pagination}
                          onChange={tableOnChange}
                          bordered
                          size="middle"
                          scroll={{ x: "max-content" }}
                        />
                      )}
                    </SkeletonTable>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      <ManpowerNormModal
        open={modalOpen.status}
        id={modalOpen.id}
        category={category}
        projectId={selectedProjectId}
        onClose={() => setModalOpen({ status: false, id: null })}
        onSuccess={() => {
          refreshData();
        }}
      />
      <ManpowerNormUploadModal
        open={uploadModalOpen}
        projectId={selectedProjectId}
        category={category}
        onClose={() => {
          setUploadModalOpen(false);
          refreshListView();
        }}
        onSuccess={refreshListView}
      />
      <ManpowerNormsHistoryDrawer
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        defaultTab={historyDefaultTab}
      />
      <ManpowerNormsExportModal
        open={exportOpen}
        projectId={selectedProjectId}
        category={category}
        onClose={() => setExportOpen(false)}
        onViewExports={() => {
          setExportOpen(false);
          setHistoryDefaultTab("exports");
          setHistoryDrawerOpen(true);
        }}
      />
    </>
  );
}

export default ManpowerNormList;
